import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CheckCircle2, Download, ExternalLink, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { VendorAvatar } from "@/components/Brand";
import { QuoteForm } from "@/components/QuoteForm";
import { Checkbox } from "@/components/ui/checkbox";

function Materials({ d }: { d: any }) {
  const { vendor, assets } = d;
  const rows = [
    ...(vendor.booking_url ? [{ key: "booking", href: vendor.booking_url, icon: CalendarCheck, label: "Book a call" }] : []),
    ...(vendor.ctas || []).map((c: any) => ({ key: `cta-${c.label}-${c.url}`, href: c.url, icon: ExternalLink, label: c.label })),
    ...assets.map((a: any) => ({ key: a.id, href: a.kind === "file" ? fileUrl(a.file_path) : a.url, icon: a.kind === "file" ? Download : ExternalLink, label: a.title })),
    ...(vendor.website ? [{ key: "website", href: vendor.website, icon: Globe, label: "Website" }] : []),
  ];
  if (!rows.length) return null;
  return (
    <div className="space-y-2" data-testid="share-success-materials">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Saved to your vendors</p>
      {rows.map((r) => (
        <a key={r.key} href={r.href} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border p-3 text-sm font-semibold hover:border-emerald-500" data-testid={`share-success-material-${r.key}`}>
          <r.icon className="h-4 w-4 text-emerald-600" /> <span className="flex-1 truncate">{r.label}</span>
        </a>
      ))}
    </div>
  );
}

export function ShareSuccess({ leadId }: { leadId: string }) {
  const [d, setD] = useState<any>(null);
  const [extrasDone, setExtrasDone] = useState(false);
  useEffect(() => { api.get(`/me/vendors/${leadId}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [leadId]);
  if (!d) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-emerald-600" />;
  const marketing = async (v: boolean) => {
    try { await api.post(`/me/vendors/${leadId}/marketing`, { consent: v }); setD({ ...d, lead: { ...d.lead, marketing_consent: v } }); } catch (e) { toast.error(errMsg(e)); }
  };
  const submitExtras = async (a: any) => {
    try { await api.post(`/me/vendors/${leadId}/quote`, { answers: a }); toast.success(`Sent to ${d.vendor.name}`); setExtrasDone(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const showExtras = d.form && !d.answers && !extrasDone;
  return (
    <div className="space-y-5" data-testid="share-success">
      <div className="flex items-center gap-3">
        <VendorAvatar vendor={d.vendor} size={44} />
        <div>
          <p className="flex items-center gap-1.5 text-lg font-bold text-emerald-700" data-testid="share-success-title"><CheckCircle2 className="h-5 w-5" /> Shared</p>
          <p className="text-sm text-slate-600">{d.vendor.name} has your {d.base ? `${d.base.event_type_name} answers` : "details"}.</p>
        </div>
      </div>
      <Materials d={d} />
      <label className="flex gap-3 rounded-xl border p-3 text-xs leading-relaxed text-slate-600">
        <Checkbox checked={!!d.lead.marketing_consent} onCheckedChange={(v) => marketing(!!v)} className="mt-0.5" data-testid="share-success-marketing-checkbox" />
        <span><b>Optional.</b> {d.marketing_text}</span>
      </label>
      {showExtras && (
        <div className="space-y-3 rounded-xl border border-dashed p-4" data-testid="share-success-extras">
          <p className="text-sm font-semibold">{d.vendor.name} has a few extra questions <span className="font-normal text-slate-500">(optional)</span></p>
          <QuoteForm form={d.form} onSubmit={submitExtras} onSkip={() => setExtrasDone(true)} submitLabel="Send answers" testid="share-extras-form" />
        </div>
      )}
      <Link to={`/me/v/${leadId}`} className="block rounded-sm bg-slate-900 py-3 text-center text-sm font-semibold text-white" data-testid="share-success-open-button">Open in my vendors</Link>
    </div>
  );
}
